import cn from 'classnames';
import { CSSProperties, FC, useContext, useEffect, useMemo, useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CountdownTimer from 'src/components/common/CountdownTimer';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { CategoryPageContext } from 'src/constructors/pageConstructors/CategoryPageConstructor';
import { formatCurrency, scrollToTop } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { MediaManager } from 'src/manager/MediaManager';
import { getCasinoTournamentInstances } from 'src/services/gosApi';
import { setSelectedCategory } from 'src/store/app/actions';
import { GosWidgetMediaProps, TournamentView } from 'src/types/common-types/gosWidget';
import { RootState } from 'src/types/store-types';
import './styles.scss';

export const GosWidget: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  const categoryPage = useContext(CategoryPageContext);
  const { sideBarMenuIsOpen } = useSelector((state: RootState) => state.app);
  const [instances, setInstances] = useState<any[]>([]);

  const templateIds = useMemo(() => {
    const gosTemplateIds = categoryPage?.pageFields?.gosTemplateIds ?? [];
    const ids: number[] = [];
    for (const item of gosTemplateIds) {
      const value = typeof item === 'object' && item !== null ? item.value : item;
      if (value == null) continue;
      const num = Number(value);
      if (!Number.isNaN(num)) ids.push(num);
    }
    return ids;
  }, [categoryPage?.pageFields?.gosTemplateIds]);

  const filteredInstances = useMemo(() => {
    if (templateIds.length === 0) return [];
    return instances.filter((item) => {
      if (item?.tournamentState !== 'lateRegistration') return false;
      return templateIds.includes(Number(item?.templateId));
    });
  }, [instances, templateIds]);

  useEffect(() => {
    if (templateIds.length === 0) return;
    scrollToTop();
    void getCasinoTournamentInstances()
      .then((res) => {
        const data = res?.result ?? res;
        setInstances(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error('Failed to load casino tournament instances', error);
      });
  }, [templateIds]);

  if (!filteredInstances.length) return null;

  return (
    <div className="gosWidget">
      {filteredInstances.map((item: any) => {
        const {
          tournamentId,
          title,
          gameImage,
          entryText,
          prizeText,
          regsText,
          backgroundUrl,
          mobileBackgroundUrl,
          isFree,
          isGtd,
          tournamentEnd,
        } = buildTournamentView(item);

        return (
          <div
            key={tournamentId ?? title}
            className={cn('gosWidget__card', {
              gosWidget__card__sidebar_open: sideBarMenuIsOpen,
            })}
            onClick={() => {
              _setSelectedCategory('casinoTournament');
              navigate('/category/casino-tournament');
            }}
            style={
              backgroundUrl || mobileBackgroundUrl
                ? ({
                    ['--gos-bg-desktop']: backgroundUrl ? `url(${backgroundUrl})` : undefined,
                    ['--gos-bg-mobile']: mobileBackgroundUrl ? `url(${mobileBackgroundUrl})` : undefined,
                  } as CSSProperties)
                : undefined
            }
          >
            <div className="gosWidget__cardInner">
              {isDesktop && <GosWidgetMedia gameImage={gameImage} title={title} />}
              <div className="gosWidget__content">
                <div className="gosWidget__badges">
                  {isFree && <span className="gosWidget__badge gosWidget__badge--free">{t('freeEntry')}</span>}
                  {isGtd && <span className="gosWidget__badge gosWidget__badge--gtd">{t('gtd')}</span>}
                  {tournamentId ? <span className="gosWidget__id">ID:{tournamentId}</span> : null}
                </div>

                <div className="gosWidget__title">
                  {isMobile && <GosWidgetMedia gameImage={gameImage} title={title} />}
                  {title}
                </div>

                <div className="gosWidget__meta">
                  <div className="gosWidget__meta_area">
                    <div className="gosWidget__metaRow">
                      <span className="gosWidget__metaLabel">{t('buyIn')}:</span>
                      <span className="gosWidget__metaValue">{entryText}</span>
                    </div>
                    <div className="gosWidget__metaRow">
                      <span className="gosWidget__metaLabel">{t('enrolled')}:</span>
                      <span className="gosWidget__metaValue">{regsText}</span>
                    </div>
                  </div>
                  <div className="gosWidget__prizeBox">
                    <span className="gosWidget__prizeLabel">{t('prize')}</span>
                    <span className="gosWidget__prizeValue">{prizeText}</span>
                  </div>
                </div>

                {tournamentEnd ? (
                  <CountdownTimer
                    classname="gosWidget__countdown"
                    targetDate={tournamentEnd}
                    view="names"
                    longNames
                    size="sm"
                    bold
                  />
                ) : null}
              </div>
              <div className="gosWidget__prize">
                <button
                  className="gosWidget__playButton"
                  type="button"
                  aria-label="Play"
                  onClick={(event) => {
                    event.stopPropagation();
                    _setSelectedCategory('casinoTournament');
                    navigate('/category/casino-tournament');
                  }}
                >
                  {isDesktop ? (
                    <SvgIcon src={icons.play} size="lg" color="white" />
                  ) : (
                    <>
                      <span>{t('play')}</span>
                      <SvgIcon src={icons.arrowRight} size="sm" />
                    </>
                  )}
                </button>
                <div className="gosWidget__icon">
                  <SvgIcon src={MediaManager.getSrcFromMediaAssets('casinoTournament.svg', '/navigation-icons/')} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GosWidget;

const buildTournamentView = (item: any): TournamentView => {
  const {
    tournamentId,
    tournamentName,
    tournamentGames,
    ui,
    prizePool,
    buyIn,
    guaranteed,
    enrolled,
    maxPlayersCount,
    registrationEndDate,
    endDate,
  } = item ?? {};

  const title = tournamentName;
  const gameImage = tournamentGames?.[0]?.thumbnail ?? null;
  const prizeAmount = prizePool?.value ?? null;
  const prizeCurrency = prizePool?.currency;
  const buyinAmount = buyIn?.buyin ?? 0;
  const buyinCurrency = buyIn?.currency;
  const isFree = Number(buyinAmount) === 0;
  const isGtd = Boolean(guaranteed?.value);
  const registered = enrolled ?? null;
  const maxPlayers = maxPlayersCount ?? null;
  const tournamentEnd = registrationEndDate ?? endDate ?? null;
  const entryText = getEntryText(isFree, buyinAmount, buyinCurrency);
  const prizeText = getPrizeText(prizeAmount, prizeCurrency);
  const regsText = getRegsText(registered, maxPlayers);
  const backgroundUrl = ui?.desktopBgUrl ?? null;
  const mobileBackgroundUrl = ui?.mobileBgUrl ?? null;

  return {
    tournamentId: tournamentId ?? null,
    title,
    gameImage,
    entryText,
    prizeText,
    regsText,
    backgroundUrl,
    mobileBackgroundUrl,
    isFree,
    isGtd,
    tournamentEnd,
  };
};

const getEntryText = (isFree: boolean, buyinAmount: unknown, currency: string): string | JSX.Element => {
  if (isFree) return 'Gratis';
  if (typeof buyinAmount === 'number') return formatCurrency(buyinAmount, currency);
  return '-';
};

const getPrizeText = (prizeAmount: unknown, currency: string): string | JSX.Element => {
  if (typeof prizeAmount === 'number') return formatCurrency(prizeAmount, currency);
  return '-';
};

const getRegsText = (registered: unknown, maxPlayers: unknown): string => {
  if (registered != null && maxPlayers != null) return `${registered}/${maxPlayers}`;
  if (registered != null) return `${registered}`;
  return '-';
};

const GosWidgetMedia: FC<GosWidgetMediaProps> = ({ gameImage, title }) => {
  return useMemo(
    () => (
      <div className="gosWidget__media">
        {gameImage ? (
          <img className="gosWidget__mediaImage" src={gameImage} alt={title} />
        ) : (
          <div className="gosWidget__mediaFallback" />
        )}
      </div>
    ),
    [gameImage, title]
  );
};
